import { wrapSelection } from "../../tools"
import TransformGizmo, { removeExistingGizmo } from "./transform-gizmo"
import HorizontalOptionsMenu from "./horizontal-options-menu"
import MediaLibrary from "aldoo-ra/MediaLibrary"
import React from "react"
import { createRoot } from "react-dom/client"
import ReactDOM from "react-dom"
import config from "aldoo-ra/Config"

// Get proper CDN static URL from config
const { CMS_STATIC_URL, PLATFORM_API_URL } = config
const STATIC_URL = CMS_STATIC_URL
  ? CMS_STATIC_URL
  : PLATFORM_API_URL + "/cms/static"

// URL utility function to generate full URL paths
export const url = (path) => {
  if (!path) return ""
  if (Array.isArray(path) && path.length > 0) {
    path = path[0]
  }
  // If the path is already a full URL, return it
  if (path.startsWith("http") || path.startsWith("blob")) return path
  // Otherwise, prepend the base URL
  return STATIC_URL + "/" + path
}

// Extract filename from path
export const filename = (path) => {
  if (!path) return ""
  // If this is youtube or vimeo, return the full URL
  if (path.includes("youtube") || path.includes("youtu.be")) return path
  return path.split("/").pop()
}

const DEFAULT_IMAGE_CONFIG = {
  maxWidth: "100%",
  display: "block",
  margin: "1rem auto",
  width: "50%",
  objectFit: "contain",
  borderRadius: "0.375rem",
}

const createImageElement = (cdnPath, alt = "") => {
  const img = document.createElement("img")

  // Set cdn attribute instead of src
  img.setAttribute("cdn", cdnPath)
  img.alt = alt || "Inserted image"
  img.classList.add("editor-image")

  // Apply default styles
  Object.entries(DEFAULT_IMAGE_CONFIG).forEach(([key, value]) => {
    img.style[key] = value
  })

  return img
}

const insertImage = (img, selectionRange, setSelectionRange) => {
  if (!selectionRange) {
    const editor = document.querySelector(".editor")
    editor.appendChild(img)
    const range = document.createRange()
    range.setStartAfter(img)
    range.setEndAfter(img)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    setSelectionRange(range)
    return
  }

  const range = selectionRange.cloneRange()
  range.deleteContents()
  range.insertNode(img)
  range.setStartAfter(img)
  range.setEndAfter(img)
  const selection = window.getSelection()
  selection.removeAllRanges()
  selection.addRange(range)
  setSelectionRange(range)
}

// Function to render MediaLibrary directly in the document
const renderMediaLibrary = (props) => {
  // Create a container for the modal if it doesn't exist
  let container = document.getElementById("media-library-container")
  let root
  
  if (!container) {
    container = document.createElement("div")
    container.id = "media-library-container"
    document.body.appendChild(container)
  }

  // Create root and render the MediaLibrary component
  root = createRoot(container)
  root.render(<MediaLibrary {...props} />)

  return {
    close: () => {
      root.unmount()
      if (container && container.parentNode) {
        container.parentNode.removeChild(container)
      }
    },
  }
}

const addImage = async (data) => {
  const { selectionRange, setSelectionRange, options = {} } = data

  // If URL is directly provided, use it
  if (options.url) {
    const img = createImageElement(options.url, options.alt)
    insertImage(img, selectionRange, setSelectionRange)

    // Add caption if provided
    if (options.caption) {
      const captionElement = document.createElement("div")
      captionElement.textContent = options.caption
      captionElement.className = "image-caption text-center text-sm mt-2 text-gray-600"
      
      // Add data attribute to image for reliable association
      const imageId = img.id || `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      if (!img.id) {
        img.id = imageId
      }
      captionElement.setAttribute("data-image-id", img.id)
      img.setAttribute("data-caption", options.caption)
      
      img.insertAdjacentElement("afterend", captionElement)
    }

    return img
  }

  // Otherwise, open MediaLibrary directly
  return new Promise((resolve) => {
    const mediaLibrary = renderMediaLibrary({
      isOpen: true,
      title: "Add Image",
      defaultFileType: "image",
      onClose: () => {
        mediaLibrary.close()
        resolve(null)
      },
      onFileSelect: (file) => {
        if (!file || (!file.path && !file.url)) {
          mediaLibrary.close()
          resolve(null)
          return
        }

        // Extract the proper path from the file path or url
        const filePath = file.path || file.url

        // Extract the cdn path (relative path without domain)
        let cdnPath = filePath

        // If it's a full URL, extract the path portion
        if (cdnPath.includes("://")) {
          try {
            const url = new URL(cdnPath)
            cdnPath = url.pathname
          } catch (e) {
            // If URL parsing fails, just use the path as is
            console.warn("Failed to parse URL, using raw path")
          }
        }

        // Remove any leading slashes for consistency
        cdnPath = cdnPath.replace(/^\/+/, "")

        // Create and insert the image
        const img = createImageElement(cdnPath, options.alt)
        insertImage(img, selectionRange, setSelectionRange)

        // Set the src attribute directly using the url function
        img.setAttribute("src", url(cdnPath))

        // Add caption if provided
        if (options.caption) {
          const captionElement = document.createElement("div")
          captionElement.textContent = options.caption
          captionElement.className = "image-caption text-center text-sm mt-2 text-gray-600"
          
          // Add data attribute to image for reliable association
          const imageId = img.id || `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          if (!img.id) {
            img.id = imageId
          }
          captionElement.setAttribute("data-image-id", img.id)
          img.setAttribute("data-caption", options.caption)
          
          img.insertAdjacentElement("afterend", captionElement)
        }

        mediaLibrary.close()
        resolve(img)
      },
    })
  })
}

const replaceImage = (data) => {
  const { selectedImage, onReplaceComplete } = data
  if (!selectedImage) return

  // Open MediaLibrary directly
  const mediaLibrary = renderMediaLibrary({
    isOpen: true,
    title: "Replace Image",
    defaultFileType: "image",
    onClose: () => {
      mediaLibrary.close()
    },
    onFileSelect: (file) => {
      if (!file || (!file.path && !file.url)) {
        mediaLibrary.close()
        return
      }

      // Extract the proper path from the file path or url
      const filePath = file.path || file.url

      // Extract the cdn path (relative path without domain)
      let cdnPath = filePath

      // If it's a full URL, extract the path portion
      if (cdnPath.includes("://")) {
        try {
          const url = new URL(cdnPath)
          cdnPath = url.pathname
        } catch (e) {
          // If URL parsing fails, just use the path as is
          console.warn("Failed to parse URL, using raw path")
        }
      }

      // Remove any leading slashes for consistency
      cdnPath = cdnPath.replace(/^\/+/, "")

      // Update the cdn attribute
      selectedImage.setAttribute("cdn", cdnPath)

      // Set the src attribute directly using the url function
      selectedImage.setAttribute("src", url(cdnPath))

      // Call the onReplaceComplete callback if provided
      if (onReplaceComplete && typeof onReplaceComplete === "function") {
        onReplaceComplete(selectedImage)
      }

      mediaLibrary.close()
    },
  })

  return mediaLibrary
}

const deleteImage = (data) => {
  const { selectedImage } = data
  if (!selectedImage) return
  
  // Remove any existing transform gizmo before deleting the image
  removeExistingGizmo()
  
  // Remove the image
  selectedImage.remove()
}

const setSize = (data) => {
  const { selectedImage, size } = data
  if (!selectedImage) return
  
  // Get the original (natural) dimensions of the image
  const originalWidth = selectedImage.naturalWidth
  
  // Calculate new dimensions as percentages of natural dimensions
  let newWidth, newHeight
  if (size.includes('%')) {
    // For percentage values, apply same percentage to both width and height
    const percentValue = size
    newWidth = percentValue
    newHeight = percentValue
  } else {
    // For pixel values, calculate what percentage this represents of natural dimensions
    const widthValue = parseInt(size.replace('px', ''))
    const widthPercent = Math.round((widthValue / originalWidth) * 100)
    newWidth = `${widthPercent}%`
    newHeight = `${widthPercent}%`
  }
  
  // Parse numeric values and round them like the transform gizmo does
  const numericWidth = newWidth.includes('%') ? newWidth : `${Math.round(parseInt(newWidth.replace('px', '')))}px`
  const numericHeight = newHeight === 'auto' || newHeight.includes('%') ? newHeight : `${Math.round(parseInt(newHeight.replace('px', '')))}px`
  

  // Check if image is in a figure container (has caption)
  const figureContainer = selectedImage.closest("figure")

  if (figureContainer) {
    // For images in figure containers, set both width and height on container and image
    // Force set both dimensions using setProperty to ensure they override any existing styles
    figureContainer.style.setProperty('width', numericWidth, 'important')
    figureContainer.style.setProperty('height', numericHeight, 'important')
    
    if (numericWidth.includes('%')) {
      selectedImage.style.width = '100%' // Image fills the figure container width
      selectedImage.style.height = '100%' // Image fills the figure container height
    } else {
      selectedImage.style.width = numericWidth
      selectedImage.style.height = numericHeight
    }
    
    
    // Remove any existing width classes from figure container
    const figureWidthClass = Array.from(figureContainer.classList).find((cls) =>
      cls.startsWith("w-")
    )
    if (figureWidthClass) {
      figureContainer.classList.remove(figureWidthClass)
    }
  } else {
    // For standalone images, apply dimensions directly
    selectedImage.style.width = numericWidth
    selectedImage.style.height = numericHeight
    
  }
  
  // Remove any existing width classes from image
  const currentWidthClass = Array.from(selectedImage.classList).find((cls) =>
    cls.startsWith("w-")
  )
  if (currentWidthClass) {
    selectedImage.classList.remove(currentWidthClass)
  }
  
  
  // Update gizmo position if gizmo exists
  const gizmo = document.querySelector(".ae-transform-gizmo")
  if (gizmo) {
    // Force a reflow to ensure the new dimensions are applied before updating gizmo
    selectedImage.offsetHeight // Force reflow
    if (figureContainer) {
      figureContainer.offsetHeight // Force reflow on container too
    }
    
    // Update gizmo position after reflow
    requestAnimationFrame(() => {
      const updateGizmoPosition = (gizmo, image) => {
        const editorContainer = document.querySelector(".editor")
        if (!editorContainer) return
        
        // Get the actual element we should position around (figure or image)
        const getTransformTarget = (image) => {
          const figureContainer = image.closest("figure")
          return figureContainer || image
        }
        
        const transformTarget = getTransformTarget(image)
        const editorRect = editorContainer.getBoundingClientRect()
        const targetRect = transformTarget.getBoundingClientRect()

        // Calculate position relative to the editor container
        const top = targetRect.top - editorRect.top + editorContainer.scrollTop
        const left = targetRect.left - editorRect.left + editorContainer.scrollLeft

        gizmo.style.left = `${left}px`
        gizmo.style.top = `${top}px`
        gizmo.style.width = `${targetRect.width}px`
        gizmo.style.height = `${targetRect.height}px`
      }
      
      updateGizmoPosition(gizmo, selectedImage)
      
      // Trigger any observers that might be watching for changes
      const event = new Event('imageResized', { bubbles: true })
      selectedImage.dispatchEvent(event)
    })
  } else {
    // Force a browser repaint to ensure the changes are applied
    requestAnimationFrame(() => {
      // Trigger any observers that might be watching for changes
      const event = new Event('imageResized', { bubbles: true })
      selectedImage.dispatchEvent(event)
    })
  }
}

const updateImageAlignment = (data) => {
  const { selectedImage, alignment } = data
  if (!selectedImage) return

  // Update image styles based on alignment
  switch (alignment) {
    case "left":
      selectedImage.style.float = "left"
      selectedImage.style.marginRight = "1rem"
      selectedImage.style.marginLeft = "0"
      break
    case "center":
      selectedImage.style.float = "none"
      selectedImage.style.margin = "1rem auto"
      selectedImage.style.display = "block"
      break
    case "right":
      selectedImage.style.float = "right"
      selectedImage.style.marginLeft = "1rem"
      selectedImage.style.marginRight = "0"
      break
  }

  // Handle caption alignment if exists
  const caption = selectedImage.nextElementSibling
  if (caption?.classList.contains("image-caption")) {
    caption.style.textAlign = alignment
  }
}

// Function to process cdn attributes on an element
export const processCDNAttributes = (element) => {
  if (!element) return

  // Process this element if it has a cdn attribute
  if (element.hasAttribute && element.hasAttribute("cdn")) {
    const cdnFile = element.getAttribute("cdn")
    // Set the src attribute using the url function
    element.setAttribute("src", url(cdnFile))
  }

  // Process all child elements recursively
  if (element.querySelectorAll) {
    const cdnElements = element.querySelectorAll("[cdn]")
    cdnElements.forEach((el) => processCDNAttributes(el))
  }
}

const editImageSource = (data) => {
  const { target } = data
  if (!target) return

  // Remove any existing dialog first
  const existingDialog = document.querySelector(".image-source-dialog-container")
  if (existingDialog) {
    existingDialog.remove()
  }

  // Import the source dialog from button plugin since it's generic enough to use
  import("../button/source-dialog.jsx").then(({ default: SourceDialog }) => {
    // Open the source edit dialog
    const container = document.createElement("div")
    container.className = "image-source-dialog-container"
    document.body.appendChild(container)

    const root = createRoot(container)

    root.render(
      React.createElement(SourceDialog, {
        buttonElement: target, // The source dialog expects buttonElement but works with any element
        onClose: (result) => {
          root.unmount()
          container.remove()
        }
      })
    )

    // Return cleanup function
    return () => {
      root.unmount()
      container.remove()
    }
  }).catch(() => {
    // Fallback: just show the HTML in an alert (not ideal but works)
    alert("Image HTML:\n" + target.outerHTML)
  })
}

const Image = ({ action, data }) => {
  switch (action) {
    case "add":
      return addImage(data)
    case "replace":
      return replaceImage(data)
    case "delete":
      return deleteImage(data)
    case "align":
      return updateImageAlignment(data)
    case "size":
      return setSize(data)
    case "transformGizmo":
      return TransformGizmo(data)
    case "optionsMenu":
      return HorizontalOptionsMenu(data)
    case "source.edit":
      return editImageSource(data)
    case "processCDN":
      return processCDNAttributes(data.element)
    default:
      console.warn(`Unknown image action: ${action}`)
      return null
  }
}

// Add static configuration
Image.config = DEFAULT_IMAGE_CONFIG

// Add utility functions
Image.utils = {
  createImageElement,
  insertImage,
  url,
  filename,
  processCDNAttributes,
}

export default Image
